/**
 * Permission constants matching the backend seed (00090_seed_system_permissions.up.sql).
 * Format: resource:action (using hyphens, matching backend codes exactly).
 * Wildcards: "students:*" matches students:read, students:write, etc.
 *            "*:*" matches everything (super admin)
 */

export const P = {
  // Tenants
  TENANTS_READ: "tenants:read",
  TENANTS_WRITE: "tenants:write",
  TENANTS_MANAGE: "tenants:manage",

  // Campuses
  CAMPUSES_READ: "campuses:read",
  CAMPUSES_WRITE: "campuses:write",
  CAMPUSES_DELETE: "campuses:delete",

  // Users
  USERS_READ: "users:read",
  USERS_WRITE: "users:write",
  USERS_DELETE: "users:delete",
  USERS_MANAGE: "users:manage",

  // Roles
  ROLES_READ: "roles:read",
  ROLES_WRITE: "roles:write",
  ROLES_DELETE: "roles:delete",
  ROLES_MANAGE: "roles:manage",

  // Permissions
  PERMISSIONS_READ: "permissions:read",

  // Students
  STUDENTS_READ: "students:read",
  STUDENTS_WRITE: "students:write",
  STUDENTS_DELETE: "students:delete",

  // Staff
  STAFF_READ: "staff:read",
  STAFF_WRITE: "staff:write",
  STAFF_DELETE: "staff:delete",

  // Guardians
  GUARDIANS_READ: "guardians:read",
  GUARDIANS_WRITE: "guardians:write",
  GUARDIANS_DELETE: "guardians:delete",

  // Academic Years
  ACADEMIC_YEARS_READ: "academic-years:read",
  ACADEMIC_YEARS_WRITE: "academic-years:write",
  ACADEMIC_YEARS_DELETE: "academic-years:delete",
  ACADEMIC_YEARS_MANAGE: "academic-years:manage",

  // Academic Terms
  ACADEMIC_TERMS_READ: "academic-terms:read",
  ACADEMIC_TERMS_WRITE: "academic-terms:write",
  ACADEMIC_TERMS_DELETE: "academic-terms:delete",
  ACADEMIC_TERMS_MANAGE: "academic-terms:manage",

  // Departments
  DEPARTMENTS_READ: "departments:read",
  DEPARTMENTS_WRITE: "departments:write",
  DEPARTMENTS_DELETE: "departments:delete",

  // Grade Levels (replaces "classes")
  GRADE_LEVELS_READ: "grade-levels:read",
  GRADE_LEVELS_WRITE: "grade-levels:write",
  GRADE_LEVELS_DELETE: "grade-levels:delete",

  // Streams (replaces "sections")
  STREAMS_READ: "streams:read",
  STREAMS_WRITE: "streams:write",
  STREAMS_DELETE: "streams:delete",

  // Subjects
  SUBJECTS_READ: "subjects:read",
  SUBJECTS_WRITE: "subjects:write",
  SUBJECTS_DELETE: "subjects:delete",

  // Curriculum
  CURRICULUM_READ: "curriculum:read",
  CURRICULUM_WRITE: "curriculum:write",
  CURRICULUM_DELETE: "curriculum:delete",

  // Rooms
  ROOMS_READ: "rooms:read",
  ROOMS_WRITE: "rooms:write",
  ROOMS_DELETE: "rooms:delete",

  // Enrollment
  ENROLLMENT_READ: "enrollment:read",
  ENROLLMENT_WRITE: "enrollment:write",
  ENROLLMENT_DELETE: "enrollment:delete",
  ENROLLMENT_MANAGE: "enrollment:manage",

  // Instructor Assignments
  ASSIGNMENTS_READ: "assignments:read",
  ASSIGNMENTS_WRITE: "assignments:write",
  ASSIGNMENTS_DELETE: "assignments:delete",

  // Timetable
  TIMETABLE_READ: "timetable:read",
  TIMETABLE_WRITE: "timetable:write",
  TIMETABLE_DELETE: "timetable:delete",

  // Grades
  GRADES_READ: "grades:read",
  GRADES_WRITE: "grades:write",
  GRADES_DELETE: "grades:delete",
  GRADES_MANAGE: "grades:manage",

  // Grading Scales (alias to grades:* — same resource)
  GRADING_SCALES_READ: "grades:read",
  GRADING_SCALES_WRITE: "grades:write",
  GRADING_SCALES_DELETE: "grades:delete",
  GRADING_SCALES_MANAGE: "grades:manage",

  // Assessments
  ASSESSMENTS_READ: "assessments:read",
  ASSESSMENTS_WRITE: "assessments:write",
  ASSESSMENTS_DELETE: "assessments:delete",

  // Report Cards
  REPORT_CARDS_READ: "report-cards:read",
  REPORT_CARDS_WRITE: "report-cards:write",
  REPORT_CARDS_PUBLISH: "report-cards:publish",

  // Attendance
  ATTENDANCE_READ: "attendance:read",
  ATTENDANCE_WRITE: "attendance:write",
  ATTENDANCE_MANAGE: "attendance:manage",

  // Fees
  FEES_READ: "fees:read",
  FEES_WRITE: "fees:write",
  FEES_DELETE: "fees:delete",
  FEES_MANAGE: "fees:manage",

  // Invoices
  INVOICES_READ: "invoices:read",
  INVOICES_WRITE: "invoices:write",
  INVOICES_DELETE: "invoices:delete",
  INVOICES_MANAGE: "invoices:write",

  // Payments
  PAYMENTS_READ: "payments:read",
  PAYMENTS_WRITE: "payments:write",
  PAYMENTS_CREATE: "payments:write",

  // Notifications
  NOTIFICATIONS_READ: "notifications:read",
  NOTIFICATIONS_WRITE: "notifications:write",
  NOTIFICATIONS_MANAGE: "notifications:manage",

  // Announcements
  ANNOUNCEMENTS_READ: "announcements:read",
  ANNOUNCEMENTS_WRITE: "announcements:write",
  ANNOUNCEMENTS_DELETE: "announcements:delete",

  // Messages
  MESSAGES_READ: "messages:read",
  MESSAGES_WRITE: "messages:write",

  // Reports
  REPORTS_READ: "reports:read",
  REPORTS_WRITE: "reports:write",
  REPORTS_EXPORT: "reports:export",

  // Exams
  EXAMS_READ: "exams:read",
  EXAMS_WRITE: "exams:write",
  EXAMS_DELETE: "exams:delete",
  EXAMS_MANAGE: "exams:manage",

  // Exam Results
  EXAM_RESULTS_READ: "exam-results:read",
  EXAM_RESULTS_WRITE: "exam-results:write",

  // Discipline
  DISCIPLINE_READ: "discipline:read",
  DISCIPLINE_WRITE: "discipline:write",
  DISCIPLINE_DELETE: "discipline:delete",
  DISCIPLINE_MANAGE: "discipline:manage",

  // Admissions
  ADMISSIONS_READ: "admissions:read",
  ADMISSIONS_WRITE: "admissions:write",
  ADMISSIONS_DELETE: "admissions:delete",
  ADMISSIONS_MANAGE: "admissions:manage",

  // Events
  EVENTS_READ: "events:read",
  EVENTS_WRITE: "events:write",
  EVENTS_DELETE: "events:delete",

  // Health
  HEALTH_READ: "health:read",
  HEALTH_WRITE: "health:write",

  // Documents
  DOCUMENTS_READ: "documents:read",
  DOCUMENTS_WRITE: "documents:write",
  DOCUMENTS_DELETE: "documents:delete",
  DOCUMENTS_MANAGE: "documents:manage",

  // System Config
  CONFIG_READ: "config:read",
  CONFIG_WRITE: "config:write",

  // Audit Logs
  AUDIT_LOGS_READ: "audit-logs:read",

  // MFA
  MFA_READ: "mfa:read",
  MFA_WRITE: "mfa:write",
  MFA_MANAGE: "mfa:manage",

  // Background Jobs
  JOBS_READ: "jobs:read",
  JOBS_WRITE: "jobs:write",
  JOBS_MANAGE: "jobs:manage",

  // Facilities
  FACILITIES_READ: "facilities:read",
  FACILITIES_WRITE: "facilities:write",
  FACILITIES_MANAGE: "facilities:manage",
  FACILITIES_BOOK: "facilities:book",

  // Hostel
  HOSTEL_READ: "hostel:read",
  HOSTEL_WRITE: "hostel:write",
  HOSTEL_MANAGE: "hostel:manage",

  // Transport
  TRANSPORT_READ: "transport:read",
  TRANSPORT_WRITE: "transport:write",
  TRANSPORT_DELETE: "transport:delete",
  TRANSPORT_MANAGE: "transport:manage",
  TRANSPORT_DISPATCH: "transport:dispatch",
  TRANSPORT_TRACK: "transport:track",

  // Admin (full access wildcard)
  ADMIN_ALL: "admin:all",

  // Payroll
  PAYROLL_READ: "payroll:read",
  PAYROLL_WRITE: "payroll:write",
  PAYROLL_MANAGE: "payroll:manage",

  // Leave
  LEAVE_READ: "leave:read",
  LEAVE_WRITE: "leave:write",
  LEAVE_MANAGE: "leave:manage",

  // Driver Portal
  DRIVER_PORTAL_READ: "driver-portal:read",
  DRIVER_PORTAL_WRITE: "driver-portal:write",

  // Student Portal
  STUDENT_PORTAL_READ: "student-portal:read",
  STUDENT_PORTAL_WRITE: "student-portal:write",

  // Instructor Portal
  INSTRUCTOR_PORTAL_READ: "instructor-portal:read",
  INSTRUCTOR_PORTAL_WRITE: "instructor-portal:write",

  // Guardian Portal
  GUARDIAN_PORTAL_READ: "guardian-portal:read",
  GUARDIAN_PORTAL_WRITE: "guardian-portal:write",

  // Staff Portal
  STAFF_PORTAL_READ: "staff-portal:read",
  STAFF_PORTAL_WRITE: "staff-portal:write",

  // Librarian Portal
  LIBRARIAN_PORTAL_READ: "librarian-portal:read",
  LIBRARIAN_PORTAL_WRITE: "librarian-portal:write",

  // Applicant Portal (self-service admissions portal for applicants)
  APPLICANT_PORTAL_READ: "applicant-portal:read",
  APPLICANT_PORTAL_WRITE: "applicant-portal:write",

  // ── Aliases kept for backward compatibility with sidebar/nav ──
  // These map old frontend names to correct backend codes
  SETTINGS_READ: "config:read",
  SETTINGS_MANAGE: "tenants:manage",
  EMPLOYEES_READ: "staff:read",
  EMPLOYEES_MANAGE: "staff:write",
  EMPLOYEES_CREATE: "staff:write",
  CLASSES_READ: "grade-levels:read",
  INVENTORY_READ: "inventory:read",
  INVENTORY_WRITE: "inventory:write",
  INVENTORY_MANAGE: "inventory:manage",
  HEALTH_MANAGE: "health:write",
  TIMETABLE_MANAGE: "timetable:write",
  ATTENDANCE_MARK: "attendance:write",
  MESSAGES_SEND: "messages:write",
  ANNOUNCEMENTS_MANAGE: "announcements:write",

  // LMS
  LMS_READ: "lms:read",
  LMS_WRITE: "lms:write",
  LMS_MANAGE: "lms:manage",
  COURSES_READ: "courses:read",
  COURSES_WRITE: "courses:write",
  COURSES_MANAGE: "courses:manage",

  // Root Portal (Super System Admin — platform-level, cross-tenant)
  ROOT_PORTAL_READ:    "root-portal:read",
  ROOT_PORTAL_WRITE:   "root-portal:write",
  ROOT_PORTAL_MANAGE:  "root-portal:manage",

  // Platform Tenants (cross-tenant CRUD by super-admin)
  PLATFORM_TENANTS_READ:    "platform-tenants:read",
  PLATFORM_TENANTS_WRITE:   "platform-tenants:write",
  PLATFORM_TENANTS_SUSPEND: "platform-tenants:suspend",
  PLATFORM_TENANTS_DELETE:  "platform-tenants:delete",

  // Platform Billing & Subscriptions
  PLATFORM_BILLING_READ:   "platform-billing:read",
  PLATFORM_BILLING_WRITE:  "platform-billing:write",
  PLATFORM_BILLING_MANAGE: "platform-billing:manage",

  // Platform Analytics & Reporting
  PLATFORM_ANALYTICS_READ: "platform-analytics:read",

  // Platform System Config / Feature Flags
  PLATFORM_CONFIG_READ:  "platform-config:read",
  PLATFORM_CONFIG_WRITE: "platform-config:write",

  // Platform Audit Log
  PLATFORM_AUDIT_READ: "platform-audit:read",

  // Platform Security & Compliance
  PLATFORM_SECURITY_READ:  "platform-security:read",
  PLATFORM_SECURITY_WRITE: "platform-security:write",

  // Platform Support / Helpdesk
  PLATFORM_SUPPORT_READ:  "platform-support:read",
  PLATFORM_SUPPORT_WRITE: "platform-support:write",

  // Library
  LIBRARY_READ:   "library:read",
  LIBRARY_WRITE:  "library:write",
  LIBRARY_MANAGE: "library:manage",

  // Platform Releases / Deployments
  PLATFORM_RELEASES_READ:  "platform-releases:read",
  PLATFORM_RELEASES_WRITE: "platform-releases:write",

  // Usalama (Safety & Security)
  USALAMA_READ:   "usalama:read",
  USALAMA_WRITE:  "usalama:write",
  USALAMA_DELETE: "usalama:delete",

  // Higher Education (university / college / tvet)
  CLASSES_WRITE:  "classes:write",
  CLASSES_DELETE: "classes:delete",
} as const;

export type Permission = (typeof P)[keyof typeof P];

/**
 * Check if a held permission satisfies a required permission.
 * Supports wildcards: "students:*" matches "students:read".
 * "admin:all" grants access to every resource check.
 */
export function hasPermission(held: string | undefined | null, required: string): boolean {
  if (!held || !required) return false;
  if (held === "*:*" || held === "admin:all") return true;
  const [hRes, hAct] = held.split(":");
  const [rRes, rAct] = required.split(":");
  const resMatch = hRes === "*" || hRes === rRes;
  const actMatch = hAct === "*" || hAct === rAct || hAct === "manage";
  return resMatch && actMatch;
}

/** @deprecated use hasPermission */
export const matchPermission = hasPermission;

/**
 * Check if a list of held permissions satisfies a required permission.
 */
export function hasAnyPermission(held: string[], required: string): boolean {
  return held.some((p) => hasPermission(p, required));
}
